import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import MultiDocumentImportDialog from '../../../multi-document-import-dialog'
import { useDataApi } from '../../../../context/DataApiContext'
import { useNotificationsProvider } from '../../../../context/NotificationsContext'
import { useDocumentImportNotification } from '../../../../context/DocumentImportNotificationContext'

ChassisDocumentImportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  resumeBatchId: PropTypes.string, // If provided, opens dialog at results step
}

export default function ChassisDocumentImportDialog({
  open,
  onClose,
  onComplete,
  resumeBatchId,
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()
  const notificationService = useDocumentImportNotification()
  const [documentTypes, setDocumentTypes] = React.useState([])

  // Notify notification service when dialog opens/closes
  React.useEffect(() => {
    if (notificationService) {
      notificationService.setDialogOpen(open)
    }
  }, [open, notificationService])

  // Fetch document types when dialog opens
  React.useEffect(() => {
    if (open) {
      fetchDocumentTypes()
    }
  }, [open])

  const fetchDocumentTypes = async () => {
    try {
      const response = await dataProvider.getList('document-type', {
        range: { page: 1, limit: 1000 },
        includeDisabled: false, // This will filter for active: true
      })
      setDocumentTypes(response.data.items || [])
    } catch (error) {
      console.error('Error fetching document types:', error)
      showNotification(
        'error',
        t('common.error-fetching-data', 'Error fetching data')
      )
    }
  }

  /**
   * Extract park numbers or chassis numbers from PDF filenames
   * Supports formats: 211155.pdf, parc_211155.pdf, SUU24116ESB029346.pdf, etc.
   */
  const handleExtractIdentifiers = async (files) => {
    return files.map((file) => {
      const filename = file.name
      const extractedInfo = extractChassisIdentifierFromFilename(filename)

      return {
        filename,
        value: extractedInfo ? extractedInfo.identifier : null,
        type: extractedInfo ? extractedInfo.type : null,
        file,
      }
    })
  }

  /**
   * Validate park numbers or chassis numbers against chassis database
   */
  const handleValidate = async (identifiers) => {
    try {
      // Separate identifiers with and without park numbers
      const identifiersWithParkNumbers = identifiers.filter((id) => id.value)
      const identifiersWithoutParkNumbers = identifiers.filter(
        (id) => !id.value
      )

      const parkNumbers = identifiersWithParkNumbers.map((id) => id.value)

      if (parkNumbers.length === 0) {
        throw new Error(
          t(
            'chassis-document-import.no-valid-identifiers',
            'No valid park or chassis numbers found'
          )
        )
      }

      // Call CATP API to validate park numbers
      const response = await dataProvider.post(
        'chassis/documents/bulk-import/validate',
        {
          data: { parkNumbers },
        }
      )

      const { valid = [], invalid = [], chassis = [] } = response.data

      // Add identifiers that couldn't be extracted to invalid list
      const invalidIdentifiers = [
        ...invalid,
        ...identifiersWithoutParkNumbers.map((id) => id.value),
      ]

      // Create detailed validation result
      const details = identifiers.map((identifier) => {
        const isValid = valid.includes(identifier.value)

        // Find chassis info by matching either park number or chassis number
        const chassisInfo = chassis.find((c) => {
          if (identifier.type === 'parkNumber') {
            return c.nParc === identifier.value
          } else if (identifier.type === 'chassisNumber') {
            return c.nChassis === identifier.value
          }
          // Fallback: try both
          return c.nParc === identifier.value || c.nChassis === identifier.value
        })

        // Determine message based on validation result
        let message
        if (!identifier.value) {
          message = t(
            'chassis-document-import.no-identifier',
            'Could not extract park or chassis number from filename'
          )
        } else if (isValid) {
          // Show park number and chassis number in details
          const parkInfo = chassisInfo?.nParc
            ? `Park: ${chassisInfo.nParc}`
            : ''
          const chassisNumberInfo = chassisInfo?.nChassis
            ? `Chassis: ${chassisInfo.nChassis}`
            : ''
          const operatorInfo = chassisInfo?.operateur
            ? ` - ${chassisInfo.operateur}`
            : ''
          message = `${parkInfo}${
            parkInfo && chassisNumberInfo ? ', ' : ''
          }${chassisNumberInfo}${operatorInfo}`
        } else {
          message = t(
            'chassis-document-import.chassis-not-found',
            'Chassis not found'
          )
        }

        return {
          identifier: identifier.value,
          message,
        }
      })

      return {
        valid,
        invalid: invalidIdentifiers,
        details,
        chassis,
      }
    } catch (error) {
      showNotification(
        'error',
        error.message || t('common.api-error', 'API error')
      )
      throw error
    }
  }

  /**
   * Upload files to S3 and get presigned URLs
   */
  const handleUpload = async (files, onProgress) => {
    try {
      const filenames = files.map((f) => f.name)

      // Step 1: Start import and get upload URLs
      const startResponse = await dataProvider.post(
        'chassis/documents/bulk-import/start',
        {
          data: { filenames },
        }
      )

      const { batchId, uploadUrls } = startResponse.data

      // Step 2: Upload each file to S3
      const uploadedFiles = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const uploadInfo = uploadUrls.find((u) => u.filename === file.name)

        if (!uploadInfo) {
          console.error(`No upload URL for file: ${file.name}`)
          continue
        }

        // Upload file to S3 using presigned URL
        await uploadFileToS3(file, uploadInfo.uploadUrl)

        uploadedFiles.push({
          filename: file.name,
          s3Key: uploadInfo.s3Key,
        })

        // Update progress
        if (onProgress) {
          onProgress({
            uploaded: i + 1,
            total: files.length,
          })
        }
      }

      return {
        batchId,
        files: uploadedFiles,
      }
    } catch (error) {
      showNotification(
        'error',
        error.message || t('common.upload-error', 'Upload error')
      )
      throw error
    }
  }

  /**
   * Start background processing of uploaded PDFs
   */
  const handleStartProcessing = async (uploadResults) => {
    try {
      const { batchId, files } = uploadResults

      // Trigger Lambda processing
      const response = await dataProvider.post(
        'chassis/documents/bulk-import/process',
        {
          data: { batchId, files },
        }
      )

      // Calculate estimated time:
      // - 1 minute baseline (includes ~30s Lambda cold start + first file)
      // - ~20 seconds per additional file
      const estimatedTime = Math.round(1 + ((files.length - 1) * 20) / 60)

      const result = {
        batchId: response.data.batchId,
        status: response.data.status,
        estimatedTime,
      }

      // Register batch for background monitoring
      // Extract original filename from first file
      const fileName = files[0]?.filename || 'Unknown file'

      if (notificationService) {
        notificationService.registerBatch(batchId, fileName)
      } else {
        console.error(
          `[ChassisDocumentImportDialog] ❌ Cannot register batch - notificationService is null!`
        )
      }

      return result
    } catch (error) {
      showNotification(
        'error',
        error.message || t('common.processing-error', 'Processing error')
      )
      throw error
    }
  }

  /**
   * Check processing progress
   * Note: Uses dataProvider.get() with cache-busting to bypass LRU cache (1 minute TTL)
   */
  const handleCheckProgress = async (batchId) => {
    try {
      // Bypass cache by using get() with cache-busting query param
      const timestamp = Date.now()
      const response = await dataProvider.get(
        `chassis/documents/bulk-import/status/${batchId}`,
        {
          query: { _t: timestamp }, // Cache-busting: unique query param per request
        }
      )

      return response.data
    } catch (error) {
      // Check if it's a 401 authentication error - let it propagate for token refresh handling
      if (error?.response?.status === 401) {
        throw error
      }

      // For other errors, log and return null to prevent dialog from breaking
      console.error('Error checking progress:', error)
      return null
    }
  }

  /**
   * Reassign document type for unrecognized documents
   */
  const handleReassignDocumentType = async (
    document,
    documentTypeId,
    batchId
  ) => {
    try {
      showNotification('info', t('common.processing', 'Processing...'))

      await dataProvider.post('chassis/documents/bulk-import/reassign', {
        data: {
          batchId: batchId,
          filename: document.filename,
          pages: document.pages,
          documentTypeId: documentTypeId,
        },
      })

      showNotification(
        'success',
        t('chassis-document-import.reassignment-success', {
          defaultValue: 'Document type assigned successfully',
        })
      )

      // Refresh the status to update the UI
      // This will be handled automatically by the polling in ResultsStep
    } catch (error) {
      showNotification(
        'error',
        error.message ||
          t('chassis-document-import.reassignment-error', {
            defaultValue: 'Failed to assign document type',
          })
      )
      throw error
    }
  }

  /**
   * Get preview URL for a document
   */
  const handleGetPreviewUrl = async (batchId, filename, pages) => {
    try {
      // Calculate the first page number (convert from 0-indexed to 1-indexed)
      const pageNumber = pages && pages.length > 0 ? pages[0] + 1 : null

      const response = await dataProvider.get(
        `chassis/documents/bulk-import/preview/${batchId}/${encodeURIComponent(
          filename
        )}`,
        {
          query: pageNumber ? { page: pageNumber } : {},
        }
      )

      return response.data.url
    } catch (error) {
      console.error('Error getting preview URL:', error)
      showNotification('error', t('common.error', 'Error getting preview'))
      throw error
    }
  }

  /**
   * Handle completion
   */
  const handleComplete = (results) => {
    const {
      progress = {},
      errors = [],
      unassignedUnrecognizedCount = 0,
    } = results
    const { totalDocumentsCreated = 0 } = progress

    // Helper to check if an error is an unrecognized document (not a real error)
    const isUnrecognizedDocumentError = (error) => {
      if (error.errors && Array.isArray(error.errors)) {
        return error.errors.every(
          (e) =>
            e.error &&
            (e.error.includes('Document non reconnu automatiquement') ||
              e.error.includes('Document type not found in database'))
        )
      }
      return (
        error.error &&
        (error.error.includes('Document non reconnu automatiquement') ||
          error.error.includes('Document type not found in database'))
      )
    }

    // Count real errors (not unrecognized documents)
    const realErrors = errors.filter(
      (error) => !isUnrecognizedDocumentError(error)
    )

    // Determine actual error state:
    // - Real errors that can't be resolved by user
    const hasRealErrors = realErrors.length > 0

    // - Unassigned unrecognized documents (could have been assigned but weren't)
    const hasUnassignedDocs = unassignedUnrecognizedCount > 0

    // Show success if no real errors and all unrecognized docs were assigned
    if (!hasRealErrors && !hasUnassignedDocs) {
      showNotification(
        'success',
        t('chassis-document-import.success', {
          count: totalDocumentsCreated,
          defaultValue: '{{count}} documents imported successfully',
        })
      )
    } else {
      // Show warning if there are real errors or unassigned documents
      const actualErrorCount = hasRealErrors
        ? realErrors.length
        : unassignedUnrecognizedCount

      showNotification(
        'warning',
        t('chassis-document-import.partial-success', {
          count: totalDocumentsCreated,
          errors: actualErrorCount,
          defaultValue: '{{count}} documents imported with {{errors}} error(s)',
        })
      )
    }

    if (onComplete) {
      onComplete(results)
    }
  }

  return (
    <MultiDocumentImportDialog
      open={open}
      onClose={onClose}
      onComplete={handleComplete}
      title={t('chassis-document-import.title', 'Import Chassis Documents')}
      steps={[
        t('chassis-document-import.step-select', 'Select PDFs'),
        t('chassis-document-import.step-validate', 'Validate Identifiers'),
        t('chassis-document-import.step-upload', 'Upload Files'),
        t('chassis-document-import.step-results', 'Processing Results'),
      ]}
      identifierLabel={t('chassis.identifier', 'Park/Chassis Number')}
      primaryColor={theme.palette.primary.main}
      onExtractIdentifiers={handleExtractIdentifiers}
      onValidate={handleValidate}
      onUpload={handleUpload}
      onStartProcessing={handleStartProcessing}
      onCheckProgress={handleCheckProgress}
      documentTypes={documentTypes}
      onReassignDocumentType={handleReassignDocumentType}
      onGetPreviewUrl={handleGetPreviewUrl}
      acceptedFileTypes={['application/pdf']}
      maxFiles={50}
      maxFileSize="100MB"
      resumeBatchId={resumeBatchId}
    />
  )
}

/**
 * Utility: Extract park number or chassis number from filename
 * @returns {Object|null} { identifier: string, type: 'parkNumber'|'chassisNumber' } or null
 */
function extractChassisIdentifierFromFilename(filename) {
  if (!filename) return null

  // Remove file extension
  const nameWithoutExt = filename.replace(/\.(pdf|PDF)$/, '')

  // Pattern for chassis numbers (alphanumeric, typically 10-20 chars)
  const chassisPattern = /([A-Z0-9]{10,20})/

  // Pattern 1: Park number at the start
  const startPattern = /^(\d+)[-_\s]?/
  const startMatch = nameWithoutExt.match(startPattern)
  if (startMatch) {
    return { identifier: startMatch[1], type: 'parkNumber' }
  }

  // Pattern 2: Park number after prefix
  const prefixPattern = /(?:parc|n|num|numero)[-_\s]?(\d+)/i
  const prefixMatch = nameWithoutExt.match(prefixPattern)
  if (prefixMatch) {
    return { identifier: prefixMatch[1], type: 'parkNumber' }
  }

  // Pattern 3: Chassis number (alphanumeric, uppercase)
  const chassisMatch = nameWithoutExt.toUpperCase().match(chassisPattern)
  if (chassisMatch) {
    return { identifier: chassisMatch[1], type: 'chassisNumber' }
  }

  // Pattern 4: Just digits
  const digitsOnlyPattern = /^(\d+)$/
  const digitsMatch = nameWithoutExt.match(digitsOnlyPattern)
  if (digitsMatch) {
    return { identifier: digitsMatch[1], type: 'parkNumber' }
  }

  // Pattern 5: Any sequence of digits (at least 4)
  const anyDigitsPattern = /(\d{4,})/
  const anyDigitsMatch = nameWithoutExt.match(anyDigitsPattern)
  if (anyDigitsMatch) {
    return { identifier: anyDigitsMatch[1], type: 'parkNumber' }
  }

  return null
}

/**
 * Utility: Upload file to S3 using presigned URL
 */
async function uploadFileToS3(file, presignedUrl) {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': 'application/pdf',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to upload ${file.name}: ${response.statusText}`)
  }

  return response
}
